import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Stories() {
  const { user } = useAuth();
  const [stories, setStories] = useState(null);
  const [disabled, setDisabled] = useState(false);

  const load = () => {
    api
      .get("/reviews/stories")
      .then(({ data }) => setStories(data))
      .catch((e) => {
        if (e.response?.status === 404) setDisabled(true);
        else setStories([]);
      });
  };

  useEffect(() => {
    load();
  }, []);

  const toggleFeature = async (id) => {
    await api.put(`/reviews/${id}/feature`);
    load();
  };

  const canFeature = (story) => user && (user.role === "admin" || user.id === story.stranger_id || user.id === story.photographer_id);

  if (disabled) {
    return (
      <div className="py-32 text-center px-5" data-testid="stories-disabled-state">
        <h1 className="font-extrabold uppercase text-2xl mb-3">Stories Aren't Available Right Now</h1>
        <p className="text-midnight-black/60 font-medium">Check back soon.</p>
      </div>
    );
  }

  return (
    <div data-testid="stories-page">
      <section className="bg-midnight-black py-16 text-center">
        <p className="text-stranger-orange text-xs font-bold tracking-[0.25em] uppercase mb-3">Stories</p>
        <h1 className="text-clean-white font-extrabold uppercase tracking-tight text-3xl sm:text-5xl">Real People. Real Stories.</h1>
      </section>
      <section className="bg-clean-white py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          {stories === null ? (
            <p className="text-center font-medium text-midnight-black/60" data-testid="stories-loading">
              Loading stories...
            </p>
          ) : stories.length === 0 ? (
            <p className="text-center font-medium text-midnight-black/60" data-testid="no-stories">
              No stories yet — be the first to share one after your session.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="stories-grid">
              {stories.map((s) => (
                <div key={s.id} className="border border-soft-gray rounded-2xl overflow-hidden" data-testid={`story-card-${s.id}`}>
                  {s.session_image && <img src={s.session_image} alt={s.session_title} className="w-full h-48 object-cover" />}
                  <div className="p-6">
                    {s.is_featured && (
                      <span className="inline-block mb-3 text-[10px] font-bold tracking-widest uppercase bg-stranger-orange text-clean-white px-3 py-1 rounded-full" data-testid={`featured-badge-${s.id}`}>
                        Featured
                      </span>
                    )}
                    <div className="flex gap-0.5 mb-3">
                      {Array.from({ length: s.rating }).map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 text-stranger-orange fill-stranger-orange" />
                      ))}
                    </div>
                    <p className="text-sm font-medium text-midnight-black/80 mb-4">&ldquo;{s.comment}&rdquo;</p>
                    <p className="text-xs font-bold uppercase tracking-wide text-midnight-black/60">
                      {s.stranger_name} · {s.session_type} Session
                    </p>
                    <p className="text-xs font-medium text-midnight-black/50 mt-1">
                      {s.photographer_name} · {s.city}, {s.state}
                    </p>
                    {canFeature(s) && (
                      <button data-testid={`toggle-feature-${s.id}`} onClick={() => toggleFeature(s.id)} className="mt-4 text-[11px] font-bold tracking-widest text-stranger-orange hover:underline">
                        {s.is_featured ? "UNFEATURE" : "FEATURE THIS STORY"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
